/**
 * TaskDetailDrawer - Abstraction layer for task detail viewing
 *
 * This component provides a consistent drawer interface for viewing task details.
 * It currently wraps the legacy TaskDetail component but can be easily swapped
 * with a new implementation without changing consuming components.
 *
 * Usage:
 * ```tsx
 * {selectedTaskId && (
 *   <TaskDetailDrawer
 *     taskId={selectedTaskId}
 *     matterId={matterId}
 *     onClose={handleClose}
 *   />
 * )}
 * ```
 *
 * To swap implementations:
 * - Replace the import and component usage inside this file
 * - Keep the same props interface
 * - All consuming components (ClaimsTasks, etc.) will automatically use the new version
 */

import { TaskDetail } from '@/components/tasks/task-detail/TaskDetail';
import { Task } from '@/services/types/client-intake-types';

export interface TaskDetailDrawerProps {
  /** Task ID to display */
  taskId: string;
  /** Matter ID (required for some task types) */
  matterId: string;
  /** Callback when drawer is closed */
  onClose: () => void;
  /** Optional: Pass task data directly to avoid refetching */
  task?: Task;
}

/**
 * TaskDetailDrawer - Right-side drawer for viewing task details
 *
 * This component handles:
 * - Sliding drawer animation from the right
 * - Click-outside-to-close behavior
 * - ESC key to close
 * - Consistent drawer styling
 *
 * Implementation notes:
 * - Currently uses legacy TaskDetail component with isModal={true}
 * - TaskDetail's RightDialogContent provides the drawer UI
 * - To use a new task detail implementation, replace TaskDetail import and usage below
 */
export const TaskDetailDrawer: React.FC<TaskDetailDrawerProps> = ({
  taskId,
  matterId,
  onClose,
  task,
}) => {
  // Legacy implementation - uses existing TaskDetail with drawer mode
  // The isModal={true} prop (or omitting it, as true is default) triggers
  // the RightDialogContent drawer behavior
  return (
    <TaskDetail
      taskId={taskId}
      matterId={matterId}
      onClose={onClose}
      task={task}
    />
  );

  // Future implementation example:
  // return <NewTaskDetail taskId={taskId} matterId={matterId} onClose={onClose} task={task} />;
};
